// Detected events on the trend: pure helpers (spans, pills, chains) and the DOM/canvas layer that draws them.
// chart.js re-exports every helper, so callers and tests keep importing from "./chart.js".

/** Windows up to this length (seconds) show pills and every event; longer ones thin out to warnings and alarms. */
export const DETAIL_MAX_S = 3600
const detailed = (windowSeconds) => windowSeconds <= DETAIL_MAX_S

export const SEVERITY_RGB = { info: [148, 163, 184], warning: [245, 158, 11], alarm: [239, 68, 68] }
export const SEVERITY_ALPHA = { info: 0.18, warning: 0.22, alarm: 0.25 }
export const SEVERITY_FILL = { info: "rgba(148,163,184,.18)", warning: "rgba(245,158,11,.22)", alarm: "rgba(239,68,68,.25)" }
export const SEVERITY_SOLID = { info: "#94a3b8", warning: "#f59e0b", alarm: "#ef4444" }
const rgba = (sev, a) => `rgba(${(SEVERITY_RGB[sev] || SEVERITY_RGB.info).join(",")},${a})`
const rank = { alarm: 0, warning: 1, info: 2 }

/** The lane an event is drawn on. A batch completion belongs to no lane; its span shows on the weight lane. */
export const laneOf = (e) => e.lane || (e.class === "batch.complete" ? "wt" : null)
const MIN_SPAN_PX = 3
/** A span wider than this share of the plot box is "long": a full-height wash would tint the lane, so it gets a top bar. */
export const LONG_SPAN_SHARE = 0.2

/** Span rects for the events drawn on `lane` that overlap [from, to], in px of a box `px` wide: [{id, x0, x1, severity}].
 *  An open event (t1 === null) runs to the right edge of the window. */
export function placeSpans(events, lane, from, to, px) {
  const out = [], k = px / (to - from)
  for (const e of events) {
    if (laneOf(e) !== lane) continue
    const t1 = e.t1 == null ? to : e.t1
    if (t1 < from || e.t0 > to) continue
    let x0 = Math.max(0, (e.t0 - from) * k), x1 = Math.min(px, (t1 - from) * k)
    if (x1 - x0 < MIN_SPAN_PX) { x1 = Math.min(px, x0 + MIN_SPAN_PX); x0 = Math.max(0, x1 - MIN_SPAN_PX) }
    out.push({ id: e.id, x0, x1, severity: e.severity })
  }
  return out
}

/** How a span is painted: wash colour (half alpha when long, doubled when highlighted), and for a long span a solid top bar. */
export function spanPaint(span, W, hl = false) {
  const long = span.x1 - span.x0 > LONG_SPAN_SHARE * W, sev = SEVERITY_ALPHA[span.severity] ? span.severity : "info"
  let a = SEVERITY_ALPHA[sev]
  if (long) a /= 2
  if (hl) a = Math.min(1, a * 2)
  return { long, fill: rgba(sev, +a.toFixed(3)), bar: long ? SEVERITY_SOLID[sev] : null, outline: hl ? SEVERITY_SOLID[sev] : null }
}

const PILL_GAP = 4
export const TAG_GUTTER = 72   // the right-edge value tags own this much of the plot box
/** Estimated pill width in px: 10 px monospace is ~6 px a glyph, plus the pill's padding and border. */
export const pillWidth = (label) => Math.ceil(label.length * 6 + 14)

/** Where a pill's left edge goes, given its event's span: inside the span (left-aligned) when it fits, else just after
 *  it; if that would reach the tag gutter, ending at the span's start. Never overlaps [W - gutter, W]. */
export function placePill(span, w, W, gutter = TAG_GUTTER) {
  const limit = W - gutter
  let x = span.x1 - span.x0 >= w + 4 ? span.x0 + 2 : span.x1 + 4
  if (x + w > limit) x = Math.max(0, span.x0 - w - 4)
  return { x: Math.max(0, Math.min(x, limit - w)) }
}

/** Greedy rows, most severe first (alarm, warning, info; then by x): each pill takes the first of `maxRows` rows whose
 *  occupied intervals leave it clear, so a later high-severity pill can still claim a free slot. A pill that fits no row is
 *  dropped. Input `{id, x, w?, label?, severity?}` (w measured, else estimated from the label). Output in x order. */
export function stackPills(pills, maxRows = 2) {
  const rows = Array.from({ length: maxRows }, () => []), out = []
  const order = pills.map((p, i) => ({ p, i })).sort((a, b) => (rank[a.p.severity] ?? 3) - (rank[b.p.severity] ?? 3) || a.p.x - b.p.x || a.i - b.i)
  for (const { p, i } of order) {
    const w = p.w != null ? p.w : pillWidth(p.label || "")
    const row = rows.findIndex((occ) => occ.every(([a, b]) => p.x >= b + PILL_GAP || p.x + w + PILL_GAP <= a))
    if (row < 0) continue
    rows[row].push([p.x, p.x + w]); out.push({ id: p.id, x: p.x, row, i })
  }
  return out.sort((a, b) => a.x - b.x || a.i - b.i).map(({ id, x, row }) => ({ id, x, row }))
}

/** One polyline per connected chain (cause/effect links, both directions) of two or more of the given events:
 *  [{id, t}] in time order (t = onset), each event once. Members with the same onset are ordered cause before effect.
 *  Links to events not in the list are ignored. */
export function chainPolylines(events) {
  const byId = new Map(events.map((e) => [e.id, e])), seen = new Set(), lines = []
  for (const e of events) {
    if (seen.has(e.id)) continue
    const comp = [], stack = [e.id]; seen.add(e.id)
    while (stack.length) {
      const cur = byId.get(stack.pop()); comp.push(cur)
      for (const n of [...(cur.causes || []), ...(cur.effects || [])]) if (byId.has(n) && !seen.has(n)) { seen.add(n); stack.push(n) }
    }
    if (comp.length < 2) continue
    comp.sort((a, b) => a.t0 - b.t0 || (a.id < b.id ? -1 : 1))
    const ordered = []
    for (let i = 0; i < comp.length;) {                 // equal onsets: Kahn's order over their own cause -> effect edges
      let j = i + 1; while (j < comp.length && Math.abs(comp[j].t0 - comp[i].t0) < 1e-6) j++
      const group = comp.slice(i, j), ids = new Set(group.map((g) => g.id)), out = new Map(group.map((g) => [g.id, []]))
      const indeg = new Map(group.map((g) => [g.id, 0]))
      for (const [from, to] of chainEdges(group)) { out.get(from).push(to); indeg.set(to, indeg.get(to) + 1) }
      const ready = group.filter((g) => indeg.get(g.id) === 0)
      while (ready.length) {
        const g = ready.shift(); ordered.push(g)
        for (const n of out.get(g.id)) { indeg.set(n, indeg.get(n) - 1); if (indeg.get(n) === 0) ready.push(group.find((x) => x.id === n)) }
      }
      for (const g of group) if (!ordered.includes(g)) ordered.push(g)   // a cycle: keep id order rather than lose events
      i = j
    }
    lines.push(ordered.map((c) => ({ id: c.id, t: c.t0 })))
  }
  return lines
}

/** The actual cause -> effect links among the given events: [[causeId, effectId]], each once. */
export function chainEdges(events) {
  const ids = new Set(events.map((e) => e.id)), seen = new Set(), out = []
  const add = (a, b) => { const k = `${a}>${b}`; if (!seen.has(k) && ids.has(a) && ids.has(b)) { seen.add(k); out.push([a, b]) } }
  for (const e of events) { for (const b of e.effects || []) add(e.id, b); for (const a of e.causes || []) add(a, e.id) }
  return out
}

/** Chains worth drawing at a window length: all of them up to an hour, beyond that only those holding a warning or worse.
 *  `events` is the full detected set (chains are found on it, never on the thinned one). */
export function keepChains(lines, events, windowSeconds) {
  if (detailed(windowSeconds)) return lines
  const sev = new Map(events.map((e) => [e.id, e.severity]))
  return lines.filter((l) => l.some((p) => sev.get(p.id) !== "info"))
}

/** One label table for a class: the card title (long) and, where a pill reads shorter, the pill (short). Nothing else spells these. */
export const CLASS_LABELS = Object.freeze({
  "dose.overshoot": "Dose overshoot", "dose.undershoot": "Dose undershoot",
  "shock.peak": "Mixer shock", "shock.cavitation": "Cavitation",
  "valve.slow_close": "Slow close", "wear.projection": "Valve wear projection",
  "tank.rate_change": "Tank rate change", "transfer.held": "Transfers held", "tank.hihi": "Tank high-high",
  "line.stopped": "Line stopped", "line.restarted": "Line restarted",
  "operator.hold": "Operator hold", "operator.rebuild": "Operator rebuild",
  "batch.complete": "Batch complete",
})
export const TANK_RATE_LABELS = Object.freeze({
  again: { short: "Falling again", long: "Level falling again" },
  stopped: { short: "Level stopped falling", long: "Level stopped falling" },
})
const isAgain = (e) => /falling again/.test(e.interpretation || "")
/** The card title for a detected event. */
export function classLabel(e) {
  if (e.class === "tank.rate_change") return TANK_RATE_LABELS[isAgain(e) ? "again" : "stopped"].long
  return CLASS_LABELS[e.class] || e.class
}

/** Short label for a detected event's pill (numbers come from its interpretation); null = no pill. */
export function pillLabel(e) {
  const num = (re) => { const m = re.exec(e.interpretation || ""); return m ? m[1] : null }
  switch (e.class) {
    case "dose.overshoot": { const n = num(/([+-]\d+\.\d+) kg/); return n ? `Overshoot ${n} kg` : "Overshoot" }
    case "dose.undershoot": { const n = num(/([+-]\d+\.\d+) kg/); return n ? `Undershoot ${n} kg` : "Undershoot" }
    case "shock.cavitation": { const n = num(/shock (\d+(?:\.\d+)?) g/); return n ? `Cavitation ${n} g` : "Cavitation" }
    case "shock.peak": { const n = num(/shock (\d+(?:\.\d+)?) g/); return n ? `Shock ${n} g` : "Shock" }
    case "valve.slow_close": { const n = num(/closed in (\d+(?:\.\d+)? s)/); return n ? `Slow close ${n}` : "Slow close" }
    case "tank.rate_change": return TANK_RATE_LABELS[isAgain(e) ? "again" : "stopped"].short
    case "transfer.held": return "Transfers held"
    case "tank.hihi": return "High-high"
    case "line.stopped": return "Line stopped"
    case "line.restarted": return "Line restarted"
    case "wear.projection": { const n = num(/due in (\d+)/); return n ? `Rebuild due in ${n}` : "Rebuild overdue" }
    case "operator.hold": return "Hold"
    case "operator.rebuild": return "Rebuilt"
    default: return null
  }
}

/** Events the trend draws spans and pills for at a window length. Past an hour only warnings and alarms (no batch completions). */
export function visibleEvents(events, windowSeconds) {
  if (detailed(windowSeconds)) return events
  return events.filter((e) => e.severity !== "info" && e.class !== "batch.complete")
}

const PILL_ROW_H = 16
const raf = (fn) => (typeof requestAnimationFrame === "function" ? requestAnimationFrame(fn) : setTimeout(fn, 16))

/**
 * The event layer for a stack of uPlot lanes.
 * @param lanes    the lane table (ids, heights)
 * @param plots    one uPlot per lane (uses `.over`)
 * @param detected the detector's events array, read on every `setView`
 * @param onChange called when highlight/hover changed and the host should repaint what it owns (the step band)
 * @param fallbackW px width to assume for a plot box that has no layout yet
 *
 * Per render the host calls `measure()` (all layout reads, before it writes anything), then `setView()` BEFORE uPlot
 * commits (so the `drawClear` hook sees this view's spans), then `draw()` once uPlot has committed.
 */
export function createEventLayer({ lanes, plots, detected, onChange = () => {}, fallbackW = () => 400, doc = typeof document !== "undefined" ? document : null }) {
  const laneIndex = Object.fromEntries(lanes.map((l, i) => [l.id, i]))
  const st = { from: 0, to: 1, dims: [], idMap: new Map(), spans: {}, chains: [], hl: null, hover: new Set(), hoverPill: null,
    transient: null, clickFns: new Set(), pending: null, destroyed: false }
  const isHl = (id) => st.hl === id || st.hover.has(id)

  const layers = lanes.map((lane, li) => {
    const over = plots[li].over
    const canvas = doc.createElement("canvas"); canvas.className = "ev-spans"
    const pills = doc.createElement("div"); pills.className = "ev-pills"
    over.insertBefore(canvas, over.firstChild); over.appendChild(pills)
    const L = { lane, over, canvas, layer: pills, pills: new Map(), key: "", hit: null }
    const onMove = (e) => { L.hit = e.clientX - over.getBoundingClientRect().left; schedule(li) }
    const onLeave = () => { L.hit = null; schedule(li) }
    over.addEventListener("mousemove", onMove); over.addEventListener("mouseleave", onLeave)
    L.off = () => { over.removeEventListener("mousemove", onMove); over.removeEventListener("mouseleave", onLeave) }
    return L
  })

  // One rAF-throttled hit test per frame against the last placeSpans output: hovering a span highlights its chain and,
  // where pills are thinned out (8 h), shows that event's pill transiently.
  function schedule(li) { if (st.pending == null) { st.pending = li; raf(flush) } else st.pending = li }
  function flush() {
    const li = st.pending; st.pending = null
    if (st.destroyed || li == null || st.hoverPill) return
    const L = layers[li]; let id = null
    if (L.hit != null) for (const sp of (st.spans[L.lane.id] || []).slice().reverse()) if (L.hit >= sp.x0 - 2 && L.hit <= sp.x1 + 2) { id = sp.id; break }
    if (id === st.transient) return
    st.transient = id; st.hover = id ? chainIds(id) : new Set(); refresh()
  }

  /** The whole connected chain an event belongs to (ids), or just itself. */
  function chainIds(id) {
    const out = new Set(), stack = [id]
    while (stack.length) { const k = stack.pop(); if (out.has(k) || !st.idMap.has(k)) continue; out.add(k); const e = st.idMap.get(k); stack.push(...(e.causes || []), ...(e.effects || [])) }
    if (!out.size) out.add(id)
    return out
  }
  function refresh() { for (const u of plots) if (typeof u.redraw === "function") u.redraw(false); draw(); onChange() }

  function measure() {
    return lanes.map((lane, li) => { const o = plots[li].over; return { w: Math.max(1, o.clientWidth || fallbackW()), h: Math.max(1, o.clientHeight || lane.height - 40) } })
  }
  function setView(from, to, dims) {
    st.from = from; st.to = to; st.dims = dims
    st.idMap = new Map(detected.map((e) => [e.id, e]))
    const vis = visibleEvents(detected, to - from)
    st.spans = {}
    lanes.forEach((lane, li) => { st.spans[lane.id] = placeSpans(vis, lane.id, from, to, dims[li].w) })
    const edges = chainEdges(detected)
    st.chains = keepChains(chainPolylines(detected), detected, to - from).map((line) => {
      const ids = new Set(line.map((p) => p.id))
      return { line, edges: edges.filter(([a, b]) => ids.has(a) && ids.has(b)) }
    })
    if (st.transient && !st.idMap.has(st.transient)) st.transient = null
  }

  /** uPlot `drawClear` hook for a lane: the washes go on uPlot's own canvas, beneath the grid and every series. */
  function drawWashes(laneId, u) {
    const li = laneIndex[laneId], spans = st.spans[laneId]
    if (li == null || !spans || !spans.length || !st.dims[li]) return
    const W = st.dims[li].w, k = u.bbox.width / W, ctx = u.ctx
    ctx.save()
    for (const sp of spans) {
      ctx.fillStyle = spanPaint(sp, W, isHl(sp.id)).fill
      ctx.fillRect(u.bbox.left + sp.x0 * k, u.bbox.top, (sp.x1 - sp.x0) * k, u.bbox.height)
    }
    ctx.restore()
  }

  function prepCanvas(L, W, H) {
    const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1, key = `${W}x${H}@${dpr}`
    if (L.key !== key) { L.canvas.width = Math.round(W * dpr); L.canvas.height = Math.round(H * dpr); L.canvas.style.height = `${H}px`; L.key = key }
    const ctx = L.canvas.getContext("2d"); ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H)
    return ctx
  }

  /** Top bars (long spans) and highlight outlines on the overlay canvas; the pills. */
  function draw() {
    if (st.destroyed || !st.dims.length) return
    const { from, to } = st, showPills = detailed(to - from)
    let hoverLost = false
    lanes.forEach((lane, li) => {
      const L = layers[li], { w: W, h: H } = st.dims[li], spans = st.spans[lane.id] || []
      const ctx = prepCanvas(L, W, H), spanOf = new Map(spans.map((s) => [s.id, s]))
      for (const sp of spans) {
        const p = spanPaint(sp, W, isHl(sp.id))
        if (p.bar) { ctx.fillStyle = p.bar; ctx.fillRect(sp.x0, 0, sp.x1 - sp.x0, 3) }
        if (p.outline) { ctx.strokeStyle = p.outline; ctx.lineWidth = 1; ctx.strokeRect(sp.x0 + .5, .5, Math.max(1, sp.x1 - sp.x0 - 1), H - 1) }
      }
      const cands = []
      for (const sp of spans) {
        const e = st.idMap.get(sp.id)
        if (!e || (!showPills && sp.id !== st.transient)) continue
        const label = pillLabel(e); if (!label) continue
        const w = pillWidth(label)
        cands.push({ id: sp.id, x: placePill(sp, w, W).x, w, label, severity: e.severity, e })
      }
      const want = new Map(stackPills(cands, 2).map((p) => [p.id, { ...p, c: cands.find((c) => c.id === p.id) }]))
      for (const [id, el] of L.pills) if (!want.has(id)) {
        el.remove(); L.pills.delete(id)
        if (st.hoverPill === id) { st.hoverPill = null; st.hover = new Set(); hoverLost = true }   // no mouseleave fires for a removed node
      }
      for (const [id, p] of want) {
        let el = L.pills.get(id)
        if (!el) {
          el = doc.createElement("button"); el.type = "button"; el.dataset.id = id
          el.addEventListener("mouseenter", () => { st.hoverPill = id; st.hover = chainIds(id); refresh() })
          el.addEventListener("mouseleave", () => { st.hoverPill = null; st.hover = new Set(); refresh() })
          el.addEventListener("click", (ev) => { ev.stopPropagation(); const e = st.idMap.get(id); if (e) for (const fn of st.clickFns) fn(e) })
          L.layer.appendChild(el); L.pills.set(id, el)
        }
        const cls = `ev-pill ev-${p.c.severity}${isHl(id) ? " hl" : ""}`, left = `${Math.round(p.x)}px`, top = `${2 + p.row * PILL_ROW_H}px`
        const title = p.c.e.interpretation || "", row = String(p.row)
        if (el.className !== cls) el.className = cls
        if (el.textContent !== p.c.label) el.textContent = p.c.label
        if (el.title !== title) el.title = title
        if (el.dataset.row !== row) el.dataset.row = row
        if (el.style.left !== left) el.style.left = left
        if (el.style.top !== top) el.style.top = top
      }
    })
    if (hoverLost) onChange()
  }

  /** Cause-and-effect connectors for the step band: an arrow on each actual cause -> effect link (rows set by lane). */
  function paintChains(ctx, box) {
    if (!st.chains.length) return
    const rowOf = (id) => { const e = st.idMap.get(id), li = e ? laneIndex[laneOf(e)] : null; return 5 + (li == null ? 2 : li) * 9 }
    for (const { line, edges } of st.chains) {
      const hl = line.some((p) => isHl(p.id)), pt = new Map(line.map((p) => [p.id, { x: box.xOf(p.t), y: rowOf(p.id) }]))
      ctx.save(); ctx.lineJoin = "round"; ctx.lineCap = "round"
      const segs = () => { ctx.beginPath(); for (const [a, b] of edges) { const A = pt.get(a), B = pt.get(b); ctx.moveTo(A.x, A.y); ctx.lineTo(B.x, B.y) } ctx.stroke() }
      ctx.strokeStyle = "rgba(5,9,17,.85)"; ctx.lineWidth = hl ? 5 : 3.6; segs()
      ctx.strokeStyle = hl ? "#ffffff" : "#e2e8f0"; ctx.lineWidth = hl ? 2.4 : 1.6; segs()
      ctx.fillStyle = hl ? "#ffffff" : "#e2e8f0"
      for (const [a, b] of edges) {                     // arrowhead at the middle of the link, pointing cause -> effect
        const A = pt.get(a), B = pt.get(b), ang = Math.atan2(B.y - A.y, B.x - A.x)
        ctx.save(); ctx.translate((A.x + B.x) / 2, (A.y + B.y) / 2); ctx.rotate(ang)
        ctx.beginPath(); ctx.moveTo(5, 0); ctx.lineTo(-3.5, -4); ctx.lineTo(-3.5, 4); ctx.closePath(); ctx.fill(); ctx.restore()
      }
      for (const p of line) { const q = pt.get(p.id), e = st.idMap.get(p.id); ctx.fillStyle = SEVERITY_SOLID[e ? e.severity : "info"]; ctx.beginPath(); ctx.arc(q.x, q.y, 2.6, 0, Math.PI * 2); ctx.fill() }
      ctx.restore()
    }
  }

  return {
    measure, setView, draw, drawWashes, paintChains,
    onEventClick(fn) { st.clickFns.add(fn); return () => st.clickFns.delete(fn) },
    highlightEvent(id) { st.hl = id == null ? null : id; refresh() },
    destroy() { st.destroyed = true; layers.forEach((L) => L.off()) },
    _state: st,   // for tests
  }
}
